import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTracking,
  addTracking,
  removeTracking,
  updateTracking,
  checkTrackingStatus,
  triggerTrackingAlerts,
  sendTestTelegramMessage,
  disconnectTelegram,
  getCurrentUser,
} from '../api/tracking'
import type { TrackingFilters } from '../types'

export const trackingKeys = {
  all: ['tracking'] as const,
  list: (filters: TrackingFilters) => [...trackingKeys.all, 'list', filters] as const,
  status: (releaseId: number) => [...trackingKeys.all, 'status', releaseId] as const,
}

export const userKeys = {
  me: ['user', 'me'] as const,
}

export function useTracking(filters: TrackingFilters = {}) {
  return useQuery({
    queryKey: trackingKeys.list(filters),
    queryFn: () => getTracking(filters),
  })
}

export function useTrackingStatus(releaseId: number | null) {
  return useQuery({
    queryKey: trackingKeys.status(releaseId!),
    queryFn: () => checkTrackingStatus(releaseId!),
    enabled: releaseId !== null,
  })
}

export function useCurrentUserProfile() {
  return useQuery({
    queryKey: userKeys.me,
    queryFn: () => getCurrentUser().then(r => r.user),
  })
}

export function useAddTrackingMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ releaseId, data }: { releaseId: number; data?: { notes?: string; target_buy_price?: number } }) =>
      addTracking(releaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingKeys.all })
    },
  })
}

export function useRemoveTrackingMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: removeTracking,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingKeys.all })
    },
  })
}

export function useUpdateTrackingMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ releaseId, data }: { releaseId: number; data: { notes?: string; target_buy_price?: number } }) =>
      updateTracking(releaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trackingKeys.all })
    },
  })
}

export function useTriggerAlertsMutation() {
  return useMutation({ mutationFn: triggerTrackingAlerts })
}

export function useTestTelegramMutation() {
  return useMutation({ mutationFn: sendTestTelegramMessage })
}

export function useDisconnectTelegramMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: disconnectTelegram,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userKeys.me }),
  })
}
